interface IconProps {
  className?: string
  active?: boolean
}

export function AdvisorIcon({ className, active }: IconProps) {
  // Center: (12, 9.5) - re-anchored down from y=8.5 to y=9.5
  // Base radius: 8.5 from center to dots
  // Dot size: r=0.6 (unchanged, only positions move)

  const center = { x: 12, y: 9.5 }
  const baseRadius = 8.5

  // Define 7 dots with angles (degrees) and radius adjustments
  const dots = [
    { angle: -90, radiusAdjust: 0, yOffset: -0.7 }, // Top (12 o'clock) - optical correction for even spacing
    { angle: -45, radiusAdjust: 0, yOffset: 0 }, // Top-right diagonal
    { angle: -135, radiusAdjust: 0, yOffset: 0 }, // Top-left diagonal
    { angle: 0, radiusAdjust: 0, yOffset: 0 }, // Right (3 o'clock)
    { angle: 180, radiusAdjust: 0, yOffset: 0 }, // Left (9 o'clock)
    { angle: 45, radiusAdjust: 0, yOffset: -0.3 }, // Bottom-right - small upward correction
    { angle: 135, radiusAdjust: 0, yOffset: -0.3 }, // Bottom-left - small upward correction
  ]

  // Compute dot positions
  const dotPositions = dots.map(({ angle, radiusAdjust, yOffset }) => {
    const angleRad = (angle * Math.PI) / 180
    const r = baseRadius + radiusAdjust
    return {
      cx: (center.x + r * Math.cos(angleRad)).toFixed(3),
      cy: (center.y + r * Math.sin(angleRad) + yOffset).toFixed(3),
    }
  })

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 -1 24 26"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Bulb silhouette (always visible) */}
      <g className="icon-base">
        {/* Bulb head */}
        <path d="M9 18h6" />
        <path d="M10 21h4" />
        <path d="M15 14c1.5-1.5 2.5-3.5 2.5-5.5A5.5 5.5 0 0 0 12 3a5.5 5.5 0 0 0-5.5 5.5c0 2 1 4 2.5 5.5v1.5a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1z" />
      </g>
      <g
        className={`icon-detail transition-opacity duration-150 ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
      >
        {/* 7 dots with explicit ring computation and targeted spacing adjustments */}
        {dotPositions.map((pos, i) => (
          <circle key={i} cx={pos.cx} cy={pos.cy} r="0.6" fill="currentColor" />
        ))}
      </g>
    </svg>
  )
}

export function BookIcon({ className, active }: IconProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Base: open book with curved pages and pronounced V-spine */}
      <g className="icon-base">
        {/* Left page - curved outward with more depth */}
        <path d="M2 5c0 0 1.5-1.5 5-1.5S12 5 12 5v14c0 0-1.5-1-5-1s-5 1-5 1V5z" />
        {/* Right page - curved outward */}
        <path d="M22 5c0 0-1.5-1.5-5-1.5S12 5 12 5v14c0 0 1.5-1 5-1s5 1 5 1V5z" />
        {/* Spine V-curve at top */}
        <path d="M12 5c0 0-0.5-1.5 0-2c0.5 0.5 0 2 0 2" />
        {/* Spine V-curve at bottom - more pronounced */}
        <path d="M12 19c0 0-1 1.5 0 2c1-0.5 0-2 0-2" />
      </g>
      {/* Detail: 2 text lines on right page with sequential writing animation */}
      <g
        className={`icon-detail transition-opacity duration-150 ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
      >
        {/* Line 1 - appears first */}
        <path
          d="M14.5 9h5"
          className="animate-write-line"
          style={{
            animationDelay: "0ms",
            strokeDasharray: "5",
            strokeDashoffset: active ? "0" : "5",
          }}
        />
        {/* Line 2 - appears second */}
        <path
          d="M14.5 12h5"
          className="animate-write-line"
          style={{
            animationDelay: "300ms",
            strokeDasharray: "5",
            strokeDashoffset: active ? "0" : "5",
          }}
        />
      </g>
    </svg>
  )
}

// BrainIcon - used for Memories nav item
// Features: elegant thin 1.5px stroke, middle lines move up on hover/active
export function BrainIcon({ className, active }: IconProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Base: brain outline - left and right hemispheres */}
      <g className="icon-base">
        <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
        <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
      </g>
      {/* Detail: internal folds (subtle vertical shift on hover - middle lines move UP) */}
      <g
        className={`icon-detail transition-transform duration-150 ease-out group-hover:-translate-y-0.5 ${active ? "-translate-y-0.5" : ""}`}
      >
        <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
        <path d="M12 18v-3" />
      </g>
    </svg>
  )
}

export function GoalsIcon({ className, active }: IconProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Compass circle (static) */}
      <g className="icon-base">
        <circle cx="12" cy="12" r="9" />
      </g>
      {/* Compass needle (rotates toward north on hover AND active) */}
      <g
        className={`icon-detail transition-transform duration-[160ms] group-hover:rotate-0 ${active ? "rotate-0" : "-rotate-12"}`}
        style={{
          transformOrigin: "12px 12px",
          transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        {/* Single-tone needle (diamond/lozenge shape) */}
        <path d="M12 6l-1.5 6h3L12 6z" fill="currentColor" stroke="none" />
        <path d="M12 18l-1.5-6h3L12 18z" fill="currentColor" stroke="none" />
      </g>
    </svg>
  )
}

export function DashboardsIcon({ className, active }: IconProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Base: axes frame */}
      <g className="icon-base">
        <path d="M3 3v18h18" />
      </g>
      {/* Detail: bars that lift on hover */}
      <g
        className={`icon-detail transition-transform duration-150 ease-out group-hover:-translate-y-0.5 ${active ? "-translate-y-0.5" : ""}`}
      >
        <path d="M7 16v-6M12 16V8M17 16v-3" />
      </g>
    </svg>
  )
}
